import * as Yup from 'yup'

import { cloneDeep } from 'lodash'
import { FileUploaderResponse } from '../../../../../../../components/widgets'
import { SCDFPlanNoSchema } from '../../../../_schema'
import { IWebFileAttachment } from '../../../../../applications'

export interface IDOCLabelItem {
  id: number /// integer($int64)
  itemNo: string
  size: string
  sizeL: string
  sizeF: string
  quantity: number // integer($int32)
  doorType: string
  visionPanel: boolean
  isDoor: boolean
  remarks: string
}

export const initDOCLabelItemValues = {
  id: 0,
  itemNo: '',
  size: '',
  sizeL: '',
  sizeF: '',
  quantity: 0,
  doorType: '',
  visionPanel: false,
  isDoor: false,
  remarks: ''
}

export interface IDOCLabelSite {
  id: number	// integer($int64)
  installerName: string
  projectName: string
  projectLocation: string
  bpNumber: string
  clientRefNo: string
  otherReference: string
  prototypeTestReport: number // integer($int64)
  trainingRecord: number // integer($int64)
  methodOfStatement: number // integer($int64)
  productConstructionDrawing: number // integer($int64)
  sitePlanDrawing: number // integer($int64)
  siteChecklist: number // integer($int64)
  setscoROI: number // integer($int64)
  prototypeTestReportAttachmentName: string
  trainingRecordAttachmentName: string
  methodOfStatementAttachmentName: string
  productConstructionDrawingAttachmentName: string
  sitePlanDrawingAttachmentName: string
  siteChecklistAttachmentName: string
  setscoROIAttachmentName: string
  itemDetails: Array<IDOCLabelItem>,

}

export const initDOCLabelSiteValues = {
  id: 0,
  installerName: '',
  projectName: '',
  projectLocation: '',
  bpNumber: '',
  clientRefNo: '',
  otherReference: '',
  prototypeTestReport: 0,
  trainingRecord: 0,
  methodOfStatement: 0,
  productConstructionDrawing: 0,
  sitePlanDrawing: 0,
  siteChecklist: 0,
  setscoROI: 0,
  prototypeTestReportAttachmentName: '',
  trainingRecordAttachmentName: '',
  methodOfStatementAttachmentName: '',
  productConstructionDrawingAttachmentName: '',
  sitePlanDrawingAttachmentName: '',
  siteChecklistAttachmentName: '',
  setscoROIAttachmentName: '',
  itemDetails: [],
}

export interface IDOCLabelApplication {

  requestMode: string,
  requestId: number,
  product: string,        // display values
  productBrand: string,   // display values
  cocNumber : string,     // display values
  companyName: string,
  // control values
  appType: string,
  certID: number,
  dlmTypeId : number,
  dlmTypeCode: string,
  threshold: number,      // threshold from certificate
  inspectionLevel: number,    // inspectionLevel from dlmType info

  sirSelected: Array<number>  // for DOC
  sirSelectedText: string     // for DOC

  /* DLMWebPhase0Model
  id: number
  applicationType : 'TYPE_APPLICATION'
  dlmType : number
  applicationCertificate: number
  */
  labelSet: number,
  model: string
  hourRating: string

  /* Derived from SiteDetails
  projectName
  projectLocation
  scdfProjectPlanNo
  installerName
  clientRefNo
  */

  podo: number
  podoAttachmentName: string
  otherAttachments: Array<FileUploaderResponse>
  approvalAttachment: number
  approvalAttachmentAttachmentName: string
  doHolder: number
  doHolderAttachmentName: string
  poHolder: number
  poHolderAttachmentName: string
  caHolder: number
  caHolderAttachmentName: string
  labelRecord: number
  labelRecordAttachmentName: string


  /* NOT USED IN THIS CONTEXT
  comment
  targetROI
  targetDOC
  docSplitArea
  policeReport
  */
  percentCompletion: number
  samplingLocation: string
  coverageArea: number
  unit: string
  totalLabelQuantity: number
  componentLocation: string
  poNO: string

  // SiteInspection Details
  // dlmWebPhase0SitesModel
  siteDetails: Array<IDOCLabelSite>,
  // resubmit fields
  note: string,
  noteAttachments: Array<IWebFileAttachment>

  hasRequiredDocs: number    // internal schema check value
  requiredSIR: number       // internal check for SIR

  isDraft?: boolean   // indicator for state = 'Draft'
  confirm?: boolean
}

export const initDOCLabelApplicationValues: IDOCLabelApplication = {
  requestMode: 'new',
  requestId: 0,
  product: '',
  productBrand: '',
  cocNumber: '',
  companyName: '',
  appType: '',
  certID: 0,
  dlmTypeId: 0,
  dlmTypeCode: '',
  threshold: 0,
  inspectionLevel: 0,
  labelSet: 0,
  model: '',
  hourRating: '',
  podo: 0,
  podoAttachmentName: '',
  otherAttachments: [],
  approvalAttachment: 0,
  approvalAttachmentAttachmentName: '',
  doHolder: 0,
  doHolderAttachmentName: '',
  poHolder: 0,
  poHolderAttachmentName: '',
  caHolder: 0,
  caHolderAttachmentName: '',
  labelRecord: 0,
  labelRecordAttachmentName: '',

  percentCompletion: 0,
  samplingLocation: '',
  coverageArea: 0,
  unit: '',
  totalLabelQuantity: 0,
  componentLocation: '',
  poNO: '',
  // SiteInspection Details
  siteDetails: [
      cloneDeep(initDOCLabelSiteValues),
  ],

  sirSelected: [],
  sirSelectedText: '',
  requiredSIR: 0,


  // resubmit fields
  note: '',
  noteAttachments: [],
  
  hasRequiredDocs: 0,
  confirm: false,
}


const schemaDLMSiteItem = Yup.object().shape({
  id: Yup.number().integer(),
  itemNo: Yup.string().trim().label('Item No'),
  size: Yup.string().trim().required('Size is required').label('Size'),
  quantity: Yup.number().integer().moreThan(0, 'Must be positive').required().typeError('Must be a number').label('Quantity'),
  isDoor: Yup.boolean(),
  sizeL: Yup.string().trim().label('Leaf Size')
  .when('isDoor', {
      is: true,
      then: (schema) => schema.required('Leaf Size required'),
  }),
  sizeF: Yup.string().trim().label('Frame Size')
  .when('isDoor', {
      is: true,
      then: (schema) => schema.required('Frame Size required'),
  }),
  doorType : Yup.string().trim().label('Leaf Type')
  .when('isDoor', {
      is: true,
      then: (schema) => schema.required('Select Leaf Type'),
  }),
  visionPanel: Yup.boolean().label('Vision Panel'),
  remarks: Yup.string().trim(),
})

const schemaDLMSiteDetails = {
  doc : Yup.object().shape({
    projectName: Yup.string().trim().required().label('Project Name'),
    projectLocation: Yup.string().trim().required().label('Project Location'),
    installerName: Yup.string().trim().label('Installer Company').required(),
    bpNumber: SCDFPlanNoSchema,
    clientRefNo: Yup.string().trim().label('Client Ref No'),
    otherReference: Yup.string().trim().label('DSTA Number'),
    prototypeTestReport: Yup.number().integer(),
    trainingRecord: Yup.number().integer(),
    methodOfStatement: Yup.number().integer(),
    productConstructionDrawing: Yup.number().integer(),
    sitePlanDrawing: Yup.number().integer(),
    siteChecklist: Yup.number().integer(),
    setscoROI: Yup.number().integer(),
    prototypeTestReportAttachmentName: Yup.string().trim(),
    trainingRecordAttachmentName: Yup.string().trim(),
    methodOfStatementAttachmentName: Yup.string().trim(),
    productConstructionDrawingAttachmentName: Yup.string().trim(),
    sitePlanDrawingAttachmentName: Yup.string().trim(),
    siteChecklistAttachmentName: Yup.string().trim(),
    setscoROIAttachmentName: Yup.string().trim(),
  }),
  siteInspect : Yup.object().shape({
    projectName: Yup.string().trim().required().label('Project Name'),
    projectLocation: Yup.string().trim().required().label('Project Location'),
    installerName: Yup.string().trim().label('Installer Company'),
    bpNumber: SCDFPlanNoSchema,
    clientRefNo: Yup.string().trim().label('Client Ref No'),
    otherReference: Yup.string().trim().label('DSTA Number'),
    prototypeTestReport: Yup.number().integer(),
    trainingRecord: Yup.number().integer(),
    methodOfStatement: Yup.number().integer(),
    productConstructionDrawing: Yup.number().integer(),
    sitePlanDrawing: Yup.number().integer(),
    siteChecklist: Yup.number().integer(),
    setscoROI: Yup.number().integer(),
    prototypeTestReportAttachmentName: Yup.string().trim(),
    trainingRecordAttachmentName: Yup.string().trim(),
    methodOfStatementAttachmentName: Yup.string().trim(),
    productConstructionDrawingAttachmentName: Yup.string().trim(),
    sitePlanDrawingAttachmentName: Yup.string().trim(),
    siteChecklistAttachmentName: Yup.string().trim(),
    setscoROIAttachmentName: Yup.string().trim(),
  }),
  label : Yup.object().shape({
    projectName: Yup.string().trim().required().label('Project Name'),    
    installerName: Yup.string().trim().label('Installer Company'),
    bpNumber: SCDFPlanNoSchema,
    clientRefNo: Yup.string().trim().label('Client Ref No'),
    otherReference: Yup.string().trim().label('DSTA Number'),
    prototypeTestReport: Yup.number().integer(),
    trainingRecord: Yup.number().integer(),
    methodOfStatement: Yup.number().integer(),
    productConstructionDrawing: Yup.number().integer(),
    sitePlanDrawing: Yup.number().integer(),
    siteChecklist: Yup.number().integer(),
    setscoROI: Yup.number().integer(),
    prototypeTestReportAttachmentName: Yup.string().trim(),
    trainingRecordAttachmentName: Yup.string().trim(),
    methodOfStatementAttachmentName: Yup.string().trim(),
    productConstructionDrawingAttachmentName: Yup.string().trim(),
    sitePlanDrawingAttachmentName: Yup.string().trim(),
    siteChecklistAttachmentName: Yup.string().trim(),
    setscoROIAttachmentName: Yup.string().trim(),
    itemDetails: Yup.array().of(schemaDLMSiteItem).min(1, 'Items Required').required()
  })
}


export const schemaDOCLabelApplication: any = Yup.object({
  requestMode: Yup.string().trim(),
  appType: Yup.string().trim().label('Application Type'),
  dlmTypeCode: Yup.string().trim(),
  threshold: Yup.number(),
  inspectionLevel: Yup.number(),
  labelSet: Yup.number().integer().label('Hour Rating')
  .when(['appType', 'dlmTypeCode'], {
    is: (appType: string, dlmTypeCode: string) => (['Label5','Label1b1','Label1b2','Label1b3','TriggerTest'].includes(appType) && dlmTypeCode === 'TYPE_LABEL'),
    then: (schema) => schema.moreThan(0, 'Select Hour Rating').required(),
  }),
  model: Yup.string().trim().required().label('Model'),
  hourRating: Yup.string().trim().label('Hour Rating')
  .when('appType', {
    is: (val: string) => (val === 'DOC' || val === 'MandatoryDOC' || val === 'TriggerTest'),
    then: (schema) => schema.matches(/^(?!0$)(?!0\.0$)(-|\d+(\.\d{1})?)$/, "Select from options.").required(),
  })
  .when(['appType', 'dlmTypeCode'], {
    is: (appType: string, dlmTypeCode: string) => (['Label5','Label1b1','Label1b2','Label1b3'].includes(appType) && dlmTypeCode === 'TYPE_LABEL'),
    then: (schema) => schema.matches(/^(?!0$)(?!0\.0$)(-|\d+(\.\d{1})?)$/, "Select from options.").required(),
  }),
  podo: Yup.number().integer().moreThan(0, 'Company PO is required.').required().label('Company PO'),
  podoAttachmentName: Yup.string().trim().label('PO File name'),
  otherAttachments: Yup.array(),
  approvalAttachment: Yup.number(),
  approvalAttachmentAttachmentName: Yup.string().trim(),
  doHolder: Yup.number().integer().label('Delivery Order'),
  doHolderAttachmentName: Yup.string().trim(),
  poHolder: Yup.number().integer(),
  poHolderAttachmentName: Yup.string().trim(),
  caHolder: Yup.number().integer(),
  caHolderAttachmentName: Yup.string().trim(),
  labelRecord: Yup.number().integer(),
  labelRecordAttachmentName: Yup.string().trim(),
  percentCompletion: Yup.number().integer().typeError('Must be a positive number between 0-100').label('Percentage of Completion')
  .when(['appType', 'dlmTypeCode'], {
    is: (appType: string, dlmTypeCode: string) => (appType === 'SiteInspect' && dlmTypeCode === 'TYPE_DOC'),
    then: (schema) => schema.min(0, 'Must be a positive number between 0-100').label('Percentage of Completion').max(100,'Must be a positive number between 0-100').label('Percentage of Completion').required(),
  }),
  samplingLocation: Yup.string().trim().label('Sampling Location'),
  coverageArea: Yup.number().typeError('Must be a number').label('Coverage Area')
  .when(['appType', 'dlmTypeCode'], {
    is: (appType: string, dlmTypeCode: string) => (appType === 'DOC' /*|| appType === 'MandatoryDOC'*/ || (appType === 'SiteInspect' && dlmTypeCode === 'TYPE_DOC')),
    then: (schema) => schema.moreThan(0, 'Coverage Area required').required(),
  })
  .when(['threshold', 'inspectionLevel', 'appType'], {
    is: (threshold: number, inspectionLevel: number, appType: string) => (inspectionLevel > 0 && threshold >= 0 && (! (['SiteInspect','TriggerTest','LabelInspect'].includes(appType)))),
    then: (schema) => schema.max(Yup.ref('threshold'), ({max}) => `Threshold (${max}) exceeded.`),
  }),
  unit: Yup.string().trim().label('Unit of Measure')
  .when('appType', {
    is: (val: string) => (val === 'DOC' || val === 'MandatoryDOC'),
    then: (schema) => schema.required(),
  }),
  // ignore maximum when request mode is resubmit/resubmit_site (quantities should not be editable)
  totalLabelQuantity: Yup.number().integer().typeError('Must be a positive number').label('Total Label Quantity')
  .when(['appType', 'dlmTypeCode', 'requestMode'], {
    is: (appType: string, dlmTypeCode: string, requestMode: string) => ((appType !== 'TriggerTest') && dlmTypeCode === 'TYPE_LABEL' && (! ['resubmit','resubmit_site','edit','edit_site'].includes(requestMode))),
    then: (schema) => schema.moreThan(0, 'Must be positive.').label('Total Label Quantity').required(),
  })
  .when('appType', {
    is: (val: string) => (val === 'Label1b2') ,
    then: (schema) => schema.moreThan(0, 'Add item details.').max(150, 'Maximum of 150 labels only').label('Total Label Quantity').required()
  })
  .when(['threshold', 'inspectionLevel', 'appType', 'requestMode'], {
    is: (threshold: number, inspectionLevel: number, appType: string, requestMode: string) => (inspectionLevel > 0 && threshold >= 0 && (! (['SiteInspect','TriggerTest','LabelInspect'].includes(appType))) && (! ['resubmit','resubmit_site','edit','edit_site'].includes(requestMode))),
    then: (schema) => schema.max(Yup.ref('threshold'), ({max}) => `Threshold (${max}) exceeded.`),
  }),
  componentLocation: Yup.string().trim().label('Component Location'),
  poNO: Yup.string().trim().label('PO NO'),
  // SiteInspection Details - varies depending on type
  siteDetails: Yup.array().min(1,'Site Details Required').required()
  .when('appType', {
    is: (val: string) => (['Label1b1','Label1b2','Label1b3','Label5'].includes(val)) ,
    then: (schema) => schema.of(schemaDLMSiteDetails.label).max(50, 'Maximum of 50 sites only')
  })
  .when('appType', {
    is: (val: string) => (['SiteInspect','TriggerTest','LabelInspect'].includes(val)),
    then: (schema) => schema.of(schemaDLMSiteDetails.siteInspect)
  })
  .when('appType', {
    is: (val: string) => (! ['SiteInspect','TriggerTest','LabelInspect','Label1b1','Label1b2','Label1b3','Label5'].includes(val)),
    then: (schema) => schema.of(schemaDLMSiteDetails.doc)
  }),
  sirSelectedText: Yup.string().trim(),
  requiredSIR: Yup.number().integer(),
  sirSelected: Yup.array().label('Select SIR')
  .when(['appType', 'requiredSIR', 'sirSelectedText'], {
    is: (appType: string, requiredSIR: number, sirSelectedText: string) => ((appType === 'DOC') && (requiredSIR > 0) && (! (sirSelectedText && sirSelectedText.trim().length > 0))),
    then: (schema) => schema.min(Yup.ref('requiredSIR'), 'Must select SIR or enter manually.').required(),
  }),  
  note: Yup.string().trim().label('Note'),
  noteAttachments: Yup.array(),
  hasRequiredDocs: Yup.number().integer()
  .when('appType', {
    is: (val: string) => (! (['SiteInspect','TriggerTest','LabelInspect','Label1b1','Label1b2'].includes(val))),
    then: (schema) => schema.moreThan(0, 'At least 1 document required.').required(),
  }),
  confirm: Yup.boolean().oneOf([true], 'Please check the box to acknowledge').required(),
});